import axios from "axios";
import * as React from "react";
import { useState, useEffect } from "react";
import tmpReact from "@Tmp/Helpers";
import { LoadingLine, Button, SocialList, SocialItem } from "..";
import CartItem from "./CartItems";
import { CartData, CartProps } from "@TmpApp/types/widgets/Cart";
import { useAxios, useSettings, useUpdateEffect } from "@Tmp/Hooks";
import { getParam, moneyFormat } from "@TmpApp/Utils";
import * as copy from "copy-to-clipboard";
import Dialog from "@Tmp/Dialog";
import { Input } from "@Tmp/Fields";
import PromotionCode from "@Tmp/PromotionCode";

const conditionalShow = (settingsProp: any, componentCondition: boolean) => {
    if (settingsProp) {
        return settingsProp === "yes";
    }

    return componentCondition;
};

const Cart: React.FC<CartProps> = ({
    disablePromotionCode,
    disableShowTotalExVat = false,
    disableShowVat = false,
    disableShowTotal = false,
    ...props
}) => {
    const tmpSettings = useSettings();

    const [data, setData] = tmpReact.useState<CartData>({ items: [] });
    const [shareLink, setShareLink] = useState<string>();
    const shared = props.settings?.cart_shared === "yes";
    const [useCartLoading, setUseCartLoading] = useState(false);

    useEffect(() => {
        if (!shared) {
            tmp.cart.reloadComponents.push(setData);
            tmp.cart.reloadComponentsData = () =>
                axios.get("/wp-content/plugins/tmp/api/cart/index.php");
        }
    }, []);

    const showTotalExVat = conditionalShow(
        props.settings?.show_total_ex_title,
        !disableShowTotalExVat
    );
    const showVat = conditionalShow(props.settings?.show_vat_title, !disableShowVat);
    const showTotal = conditionalShow(props.settings?.show_total_title, !disableShowTotal);

    const [loading, setLoading] = useState(false);

    const {
        status: fetchStatus,
        value: fetchValue,
        run: getData,
    } = useAxios(
        "get",
        "/wp-content/plugins/tmp/api/cart/index.php",
        { shared: shared ? getParam("shared") ?? "" : undefined },
        []
    );

    useUpdateEffect(() => {
        if (fetchStatus === "loading") {
            setLoading(true);
            return;
        }
        if (fetchStatus === "success") {
            setData(fetchValue?.data?.data);
        }
        setLoading(false);
        triggerAnyChange();
    }, [fetchStatus]);

    const isTracked = React.useRef(false);

    useEffect(() => {
        if (data.items?.length > 0 && isTracked.current === false) {
            isTracked.current = true;
            tmp.gtag("event", "view_cart", {
                items: data.items.map((item, index) => ({
                    item_id: item.sku,
                    item_name: item.name,
                    index: index,
                    price: item.price_info.price,
                    quantity: item.qty,
                })),
            });
        }
    }, [data.items]);

    const triggerAnyChange = () => {
        if (props?.onAnyChange) {
            props?.onAnyChange();
        }
    };

    const triggerItemChange = () => {
        if (props?.onItemChange) {
            props?.onItemChange();
        }
    };

    const handleEmpty = () => {
        if (!data.items?.length) {
            return;
        }
        setData({ items: [] });
        setLoading(true);
        axios
            .delete("/wp-json/tmp/v1/cart")
            .then((response) => {
                if (response.data.code === "success") {
                    getData();
                    tmp.cart.count();
                } else {
                    setLoading(false);
                }
            })
            .catch((error) => {
                setLoading(false);
                tmp.notice(error?.response?.data?.message);
            })
            .finally(() => {
                triggerAnyChange();
                triggerItemChange();
            });
    };

    const handleUseCart = () => {
        setUseCartLoading(true);
        axios
            .post("/wp-content/plugins/tmp/api/cart/importFromShared.php", {
                shared: getParam("shared"),
            })
            .then((response) => {
                if (response.status === 200) {
                    tmp.notice(props.settings?.cart_shared_cart_added_text);
                    tmp.cart.count();
                }
            })
            .catch((error) => {
                tmp.notice(props.settings?.cart_shared_cart_added_failed_text);
            })
            .finally(() => {
                setUseCartLoading(false);
            });
    };

    const handleShare = () => {
        axios.post("/wp-content/plugins/tmp/api/cart/shared.php").then((response) => {
            if (response.status === 200 && response.data.link) {
                setShareLink(response.data.link);
            }
        });
    };

    const content = () => (
        <>
            {data.items?.map((item, index) => (
                <CartItem
                    key={item.id}
                    index={index}
                    settings={props.settings}
                    lastUpdate={data.lastUpdate}
                    shared={shared}
                    item={item}
                    setData={setData}
                    setLoading={setLoading}
                    getData={getData}
                    triggerAnyChange={triggerAnyChange}
                    triggerItemChange={triggerItemChange}
                />
            ))}
            {data?.shipping && (
                <div className="tmp-cart__sum">
                    <span>{data?.shipping?.name}</span>
                    <div>
                        <span className="tmp-cart__sum__total-ex-vat">{data?.shipping?.price}</span>
                        <span>{tmpSettings?.currency}</span>
                    </div>
                </div>
            )}
            {data?.promotion_codes?.length > 0 && (
                <div className="tmp-cart__promotion-codes">
                    {data?.promotion_codes.map((promotion_code) => (
                        <div className="tmp-cart__promotion-code" key={promotion_code.id}>
                            <span className="tmp-cart__promotion-code__title">
                                {promotion_code.name}
                            </span>
                            <div>
                                <span className="tmp-cart__promotion-code__amount">
                                    -{moneyFormat(promotion_code.discount)}
                                </span>
                                <span className="tmp-cart__promotion-code__currency">
                                    {tmpSettings?.currency}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {showTotalExVat && (
                <div className="tmp-cart__sum">
                    <span>{props?.settings?.total_ex_title ?? "Total Pris ekskl. moms"}</span>
                    <div>
                        <span className="tmp-cart__sum__total-ex-vat">{data?.total_ex_vat}</span>
                        <span>{tmpSettings?.currency}</span>
                    </div>
                </div>
            )}
            {showVat && (
                <div className="tmp-cart__sum" style={{ marginTop: 0 }}>
                    <span>{props?.settings?.vat_title ?? "Moms 25%"}</span>
                    <div>
                        <span className="tmp-cart__sum__vat">{data?.vat}</span>
                        <span>{tmpSettings?.currency}</span>
                    </div>
                </div>
            )}
            {showTotal && (
                <div className="tmp-cart__sum">
                    <div className="tmp-cart__sum__title">
                        {props.settings?.total_title ?? "Total pris inkl. moms"}
                    </div>
                    <div className="tmp-cart__sum__price">
                        <span className="tmp-cart__sum__total">{data?.total}</span>
                        <span className="tmp-cart__sum__currency">{tmpSettings?.currency}</span>
                    </div>
                </div>
            )}
            {renderSharedButton("bottom")}
        </>
    );

    const renderSharedButton = (placement: "top" | "bottom") => {
        return (
            shared && (
                <div>
                    <Button
                        fullWidth
                        style={
                            placement === "top" ? { marginBottom: "2rem" } : { marginTop: "2rem" }
                        }
                        onClick={handleUseCart}
                        loading={useCartLoading}
                    >
                        {props.settings?.cart_use_shared_button_text ?? "Brug kurv"}
                    </Button>
                </div>
            )
        );
    };

    const [copied, setCopied] = useState(false);

    const emailBodyText = [
        "En bekendt har sendt dig denne ønskeliste fra hjemmesiden " + tmpSettings.domain,
        "",
        "Hvis du ønsker at se ønskelisen, skal du blot klikke på dette link:",
        shareLink,
        "",
        "",
        "Alle køretøjer, udstyr og reservedele på " +
            tmpSettings.domain +
            " importeres i Danmark af:",
        "",
        "Thomas Møller Pedersen Aps.",
        "Elmevej 18, Glyngøre 7870 Roslev",
        "Email: info@tmp.dk",
        "Tel: +45 97740733",
        "CVR: 29625425",
        "",
        "Telefontider:",
        "Mandag - Torsdag: 09:00 - 16:00",
        "Fredag: 09:00 - 15:30",
        "Weekend: Lukket",
    ].join("%0D%0A");

    return (
        <React.Fragment>
            {loading && <LoadingLine />}
            <div className="tmp-cart__container">
                <div className="tmp-cart__table">
                    {!disablePromotionCode && data?.items?.length > 0 && <PromotionCode />}
                    {data?.items?.length > 0 && renderSharedButton("top")}
                    {data?.items?.length > 0 && !shared && (
                        <div className="tmp-cart__empty__button-container">
                            <div
                                className="tmp-cart__share__button cursor-pointer"
                                onClick={handleShare}
                            >
                                <i className="fas fa-share fa-lg" />
                                <span className="tmp-cart__share__button-text">
                                    {props.settings?.cart_share_button_text ?? "Del som ønskeliste"}
                                </span>
                            </div>
                            <div className="tmp-cart__empty__button" onClick={() => handleEmpty()}>
                                <span className="tmp-cart__empty__button-text">
                                    {props?.settings?.empty_button_text ?? "Tøm kurv"}
                                </span>
                                <i style={{ marginLeft: 6 }} className="fas fa-trash-alt fa-lg" />
                            </div>
                        </div>
                    )}
                    {!data?.items?.length ? (
                        <div
                            className="tmp-cart__empty"
                            dangerouslySetInnerHTML={{
                                __html: props?.settings?.empty_text,
                            }}
                        ></div>
                    ) : (
                        content()
                    )}
                </div>
            </div>
            <Dialog
                autoHeight
                open={!!shareLink}
                setOpen={() => {
                    setShareLink(undefined);
                    setCopied(false);
                }}
                Dialog={{ style: { width: "40ch" } }}
                header={<h4>{props.settings?.cart_share_dialog_title}</h4>}
                classes={{ container: "tmp-cart__share__dialog__container" }}
            >
                <div style={{ padding: "1rem" }}>
                    <SocialList style={{ marginBottom: "1rem" }}>
                        <SocialItem
                            icon="fas fa-envelope"
                            label="Email"
                            href={
                                "mailto:?body=" +
                                emailBodyText +
                                "&subject=Delt ønskeliste fra " +
                                tmpSettings.domain
                            }
                        />
                    </SocialList>
                    <Input
                        value={shareLink}
                        input={{ readOnly: true }}
                        iconAfter={
                            <span
                                className="tmp-input-icon-container cursor-pointer"
                                style={{ fontSize: "0.9rem" }}
                                onClick={() => {
                                    copy(shareLink);
                                    tmp.notice("Link copied");
                                    setCopied(true);
                                }}
                            >
                                {copied ? "KOPIERET" : "KOPIER"}
                            </span>
                        }
                    />
                </div>
            </Dialog>
        </React.Fragment>
    );
};

function Items(props) {
    return;
}

export default Cart;
